import React from 'react';
import PlaceDetailsPanel from './PlaceDetailsPanel';
import MapPlaceOverlay from './MapPlaceOverlay';
import {
  PlaceDetails,
} from './placeDetails';
import {
  ensurePlacesLibraryLoaded,
  fetchPlaceDetailsFromGoogle,
  fetchPlaceGeometryFromGoogle,
  getPlaceLocation,
  normalizePlaceId,
  resolvePlaceIdFromGoogle,
} from './googlePlacesService';
import {
  fetchDirectionsPath,
  getLegTravelMode,
  MapTravelMode,
} from './googleDirectionsService';
import './MapPlaceSidebar.css';

export interface TripMapTravelLeg {
  mode?: string;
  chosenMode?: string;
}

export interface TripMapActivity {
  id: string;
  day: string;
  description: string;
  place?: string;
  placeId?: string; // real Google place_id from the backend (location.place_id)
  startTime?: string;
  travelFromPrev?: TripMapTravelLeg;
}

interface TripMapSidebarProps {
  destinations: string[];
  activities: TripMapActivity[];
  days: string[];
  selectedDays: string[];
  selectedActivityId: string | null;
  onSelectActivity: (activityId: string) => void;
  onClearPlace?: () => void;
  mapOnly?: boolean;
  className?: string;
  selectedExternalPlaceId?: string | null;
  selectedExternalPlaceQuery?: string;
}

interface ActivityMarkerRecord {
  activityId: string;
  marker: any;
  placeId?: string;
}

interface PlottedStop {
  activity: TripMapActivity;
  position: { lat: number; lng: number };
}

const sortActivitiesByDayAndTime = (
  items: TripMapActivity[],
  dayOrder: string[],
): TripMapActivity[] => [...items].sort((left, right) => {
  const dayDiff = dayOrder.indexOf(left.day) - dayOrder.indexOf(right.day);
  if (dayDiff !== 0) return dayDiff;
  return (left.startTime || '').localeCompare(right.startTime || '');
});

const TripMapSidebar: React.FC<TripMapSidebarProps> = ({
  destinations,
  activities,
  days,
  selectedDays,
  selectedActivityId,
  onSelectActivity,
  onClearPlace,
  mapOnly = false,
  className,
  selectedExternalPlaceId = null,
  selectedExternalPlaceQuery,
}) => {
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = React.useRef<any>(null);
  const activityMarkersRef = React.useRef<ActivityMarkerRecord[]>([]);
  const routePolylinesRef = React.useRef<any[]>([]);
  const activityPlaceIdsRef = React.useRef<Map<string, string>>(new Map());
  const pendingPlaceRequestRef = React.useRef(0);

  const [mapType, setMapType] = React.useState<'roadmap' | 'satellite'>('roadmap');
  const [mapReady, setMapReady] = React.useState(false);
  const [selectedPlace, setSelectedPlace] = React.useState<PlaceDetails | null>(null);
  const [isLoadingPlace, setIsLoadingPlace] = React.useState(false);
  const [mappedActivitiesCount, setMappedActivitiesCount] = React.useState(0);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasApiKey = Boolean(googleMapsApiKey);

  const selectedDaysKey = selectedDays.join('|');

  const selectedDaysActivities = React.useMemo(
    () => sortActivitiesByDayAndTime(
      activities.filter((activity) => selectedDays.includes(activity.day)),
      days,
    ),
    [activities, days, selectedDays],
  );

  const preferredTravelMode = React.useMemo(() => {
    if (selectedDaysActivities.length === 0) return 'drive' as MapTravelMode;
    const lastActivity = selectedDaysActivities[selectedDaysActivities.length - 1];
    return getLegTravelMode(lastActivity.travelFromPrev);
  }, [selectedDaysActivities]);

  const locationContext = React.useMemo(
    () => destinations.filter(Boolean).join(', '),
    [destinations]
  );

  const clearRoutePolylines = React.useCallback(() => {
    routePolylinesRef.current.forEach((polyline) => polyline.setMap(null));
    routePolylinesRef.current = [];
  }, []);

  const clearActivityMarkers = React.useCallback(() => {
    activityMarkersRef.current.forEach(({ marker }) => marker.setMap(null));
    activityMarkersRef.current = [];
    clearRoutePolylines();
    setMappedActivitiesCount(0);
  }, [clearRoutePolylines]);

  const drawRouteSegment = React.useCallback(async (
    from: { lat: number; lng: number },
    to: { lat: number; lng: number },
    mode: MapTravelMode,
  ) => {
    if (!mapInstanceRef.current) return;

    const path = await fetchDirectionsPath(from, to, mode);
    const routePath = path?.length
      ? path
      : [from, to];

    if (!mapInstanceRef.current || !window.google?.maps?.Polyline) return;

    routePolylinesRef.current.push(new window.google.maps.Polyline({
      map: mapInstanceRef.current,
      path: routePath,
      geodesic: !path?.length,
      strokeColor: '#16563f',
      strokeOpacity: path?.length ? 0.85 : 0.55,
      strokeWeight: path?.length ? 4 : 3,
    }));
  }, []);

  const getMapElement = React.useCallback(
    () => mapRef.current || mapInstanceRef.current,
    [],
  );

  const fetchPlaceDetailsById = React.useCallback(async (
    placeId: string,
    queryFallback?: string,
    locationBias?: { lat: number; lng: number },
  ): Promise<PlaceDetails | null> => {
    const mapElement = getMapElement();
    if (!mapElement) return null;

    const placesReady = await ensurePlacesLibraryLoaded();
    if (!placesReady) return null;

    const requestId = pendingPlaceRequestRef.current + 1;
    pendingPlaceRequestRef.current = requestId;
    setIsLoadingPlace(true);

    const details = await fetchPlaceDetailsFromGoogle(mapElement, placeId, {
      queryFallback,
      locationBias,
    });

    if (requestId !== pendingPlaceRequestRef.current) return null;

    setIsLoadingPlace(false);

    if (!details) {
      setSelectedPlace(null);
      return null;
    }

    setSelectedPlace(details);

    const location = getPlaceLocation(details);
    if (location && mapInstanceRef.current) {
      mapInstanceRef.current.panTo(location);
      mapInstanceRef.current.setZoom(15);
    }

    return details;
  }, [getMapElement]);

  const resolveActivityPlaceId = React.useCallback(async (activity: TripMapActivity) => {
    // Prefer the real Google place_id the backend already stored on the stop
    // (location.place_id). It resolves the exact place — no fuzzy text match —
    // and works on the first activity click without any prior chat selection.
    const knownPlaceId = normalizePlaceId(activity.placeId);
    if (knownPlaceId) {
      return Promise.resolve(knownPlaceId);
    }

    const cachedPlaceId = activityPlaceIdsRef.current.get(activity.id);
    if (cachedPlaceId) {
      return Promise.resolve(cachedPlaceId);
    }

    // No stored id (e.g. a manual "Camper Return" stop with only text) — fall
    // back to a location-biased text search and cache whatever it resolves.
    if (!mapInstanceRef.current) {
      return Promise.resolve(null);
    }

    const placesReady = await ensurePlacesLibraryLoaded();
    if (!placesReady) {
      return null;
    }

    const queryBase = activity.place?.trim() || activity.description.trim();
    if (!queryBase) {
      return null;
    }

    const query = locationContext ? `${queryBase}, ${locationContext}` : queryBase;
    const mapCenter = mapInstanceRef.current.getCenter();

    const resolvedPlaceId = await resolvePlaceIdFromGoogle(
      getMapElement(),
      query,
      mapCenter
        ? { lat: mapCenter.lat(), lng: mapCenter.lng() }
        : undefined,
    );

    if (resolvedPlaceId) {
      activityPlaceIdsRef.current.set(activity.id, resolvedPlaceId);
    }

    return resolvedPlaceId;
  }, [getMapElement, locationContext]);

  const loadSelectedActivityPlace = React.useCallback(async (activityId: string | null) => {
    if (!activityId) {
      pendingPlaceRequestRef.current += 1;
      setSelectedPlace(null);
      setIsLoadingPlace(false);
      return;
    }

    const activity = activities.find((item) => item.id === activityId);
    if (!activity) {
      setSelectedPlace(null);
      return;
    }

    setIsLoadingPlace(true);
    const placeId = await resolveActivityPlaceId(activity);
    const queryFallback = activity.place || activity.description;
    const mapCenter = mapInstanceRef.current?.getCenter();

    if (!placeId) {
      setIsLoadingPlace(false);
      setSelectedPlace({
        name: queryFallback,
        formatted_address: locationContext || 'Location details unavailable',
      });
      return;
    }

    const details = await fetchPlaceDetailsById(
      placeId,
      queryFallback,
      mapCenter
        ? { lat: mapCenter.lat(), lng: mapCenter.lng() }
        : undefined,
    );

    if (!details) {
      setSelectedPlace({
        name: queryFallback,
        formatted_address: locationContext || 'Location details unavailable',
      });
    }
  }, [activities, fetchPlaceDetailsById, locationContext, resolveActivityPlaceId]);

  const plotDayActivities = React.useCallback(async () => {
    if (!mapInstanceRef.current || !window.google?.maps?.Marker) return;

    const placesReady = await ensurePlacesLibraryLoaded();
    if (!placesReady) return;

    clearActivityMarkers();

    if (selectedDaysActivities.length === 0) {
      if (destinations.length > 0 && window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: destinations[0] }, (results: any, status: string) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            mapInstanceRef.current.setCenter(results[0].geometry.location);
            mapInstanceRef.current.setZoom(11);
          }
        });
      }
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    const plottedStops: PlottedStop[] = [];

    for (let index = 0; index < selectedDaysActivities.length; index += 1) {
      const activity = selectedDaysActivities[index];
      const placeId = await resolveActivityPlaceId(activity);
      if (!placeId || !mapInstanceRef.current) continue;

      const geometry = await fetchPlaceGeometryFromGoogle(
        getMapElement(),
        placeId,
        {
          queryFallback: activity.place || activity.description,
          locationBias: mapInstanceRef.current.getCenter()
            ? {
                lat: mapInstanceRef.current.getCenter()!.lat(),
                lng: mapInstanceRef.current.getCenter()!.lng(),
              }
            : undefined,
        },
      );
      if (!geometry || !mapInstanceRef.current) continue;

      const position = { lat: geometry.lat, lng: geometry.lng };
      const marker = new window.google.maps.Marker({
        map: mapInstanceRef.current,
        position,
        title: activity.description,
        label: {
          text: String(index + 1),
          color: '#ffffff',
          fontWeight: '700',
        },
      });

      marker.addListener('click', () => {
        onSelectActivity(activity.id);
      });

      activityMarkersRef.current.push({
        activityId: activity.id,
        marker,
        placeId: geometry.resolvedPlaceId,
      });
      plottedStops.push({ activity, position });
      bounds.extend(position);
    }

    if (plottedStops.length >= 2) {
      for (let index = 0; index < plottedStops.length - 1; index += 1) {
        const from = plottedStops[index];
        const to = plottedStops[index + 1];
        const legMode = getLegTravelMode(to.activity.travelFromPrev, preferredTravelMode);
        await drawRouteSegment(from.position, to.position, legMode);
      }
    }

    setMappedActivitiesCount(plottedStops.length);

    if (plottedStops.length > 1) {
      mapInstanceRef.current.fitBounds(bounds, 48);
    } else if (plottedStops.length === 1) {
      mapInstanceRef.current.setCenter(bounds.getCenter());
      mapInstanceRef.current.setZoom(14);
    }
  }, [
    clearActivityMarkers,
    selectedDaysActivities,
    preferredTravelMode,
    destinations,
    drawRouteSegment,
    getMapElement,
    onSelectActivity,
    resolveActivityPlaceId,
  ]);

  const loadSelectedExternalPlace = React.useCallback(async (
    placeId: string,
    queryFallback?: string,
  ) => {
    const mapCenter = mapInstanceRef.current?.getCenter();
    const details = await fetchPlaceDetailsById(
      placeId,
      queryFallback,
      mapCenter
        ? { lat: mapCenter.lat(), lng: mapCenter.lng() }
        : undefined,
    );

    if (!details && queryFallback) {
      setSelectedPlace({
        name: queryFallback,
        formatted_address: locationContext || 'Location details unavailable',
      });
    }
  }, [fetchPlaceDetailsById, locationContext]);

  React.useEffect(() => {
    if (!hasApiKey || !mapRef.current) return undefined;
    if (mapInstanceRef.current) {
      // Remount with a surviving map (StrictMode dev double-mount): the ref
      // persists but state reset — re-arm mapReady or every place fetch stays
      // gated off forever.
      setMapReady(true);
      return undefined;
    }

    let cancelled = false;
    let pollTimer: number | undefined;

    const initMap = () => {
      if (cancelled || !window.google?.maps?.Map || !mapRef.current || mapInstanceRef.current) return;

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 38.7223, lng: -9.1393 },
        zoom: 11,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      // Set ready synchronously with map creation — an await here lets a
      // StrictMode cleanup interleave (cancelled flips) and deadlock mapReady
      // while the ref stays set. Places readiness is awaited by every fetch
      // path itself; this call just warms it up.
      setMapReady(true);
      void ensurePlacesLibraryLoaded();
    };

    const startInit = () => {
      if (cancelled || !mapRef.current) return;
      initMap();
    };

    if (window.google?.maps?.Map) {
      startInit();
    } else {
      pollTimer = window.setInterval(() => {
        if (window.google?.maps?.Map) {
          window.clearInterval(pollTimer);
          startInit();
        }
      }, 200);
    }

    return () => {
      cancelled = true;
      if (pollTimer) {
        window.clearInterval(pollTimer);
      }
    };
  }, [hasApiKey]);

  React.useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setMapTypeId(mapType);
  }, [mapType]);

  React.useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return undefined;

    plotDayActivities();

    return () => {
      clearActivityMarkers();
    };
  }, [clearActivityMarkers, mapReady, plotDayActivities, selectedDaysKey]);

  React.useEffect(() => {
    if (!mapReady) return;
    if (selectedExternalPlaceId) return;
    loadSelectedActivityPlace(selectedActivityId);
  }, [loadSelectedActivityPlace, mapReady, selectedActivityId, selectedExternalPlaceId]);

  React.useEffect(() => {
    if (!mapReady || !selectedExternalPlaceId) return;
    void loadSelectedExternalPlace(selectedExternalPlaceId, selectedExternalPlaceQuery);
  }, [loadSelectedExternalPlace, mapReady, selectedExternalPlaceId, selectedExternalPlaceQuery]);

  React.useEffect(() => {
    activityPlaceIdsRef.current.clear();
  }, [selectedDaysKey, locationContext]);

  const badgeLabel = mappedActivitiesCount === 1
    ? '1 place on map'
    : `${mappedActivitiesCount} places on map`;

  const selectedActivity = React.useMemo(
    () => activities.find((activity) => activity.id === selectedActivityId) || null,
    [activities, selectedActivityId],
  );

  const showMapOverlay = mapOnly && Boolean(selectedActivityId || selectedExternalPlaceId);
  const overlayFallbackTitle = selectedActivity?.place || selectedActivity?.description || selectedExternalPlaceQuery;

  const handleCloseOverlay = () => {
    pendingPlaceRequestRef.current += 1;
    setSelectedPlace(null);
    setIsLoadingPlace(false);
    onClearPlace?.();
  };

  return (
    <div
      className={`v2-map-sidebar ${mapOnly ? 'v2-map-sidebar--compact' : ''} ${className || ''}`.trim()}
      aria-label={mapOnly ? 'Trip map' : 'Map and place details'}
    >
      <div className="v2-map-card">
        {hasApiKey ? (
          <>
            <div ref={mapRef} className="v2-map-card__canvas" />
            {showMapOverlay && <div className="v2-map-card__dim" aria-hidden />}
            {showMapOverlay && (
              <MapPlaceOverlay
                place={selectedPlace}
                isLoading={isLoadingPlace}
                fallbackTitle={overlayFallbackTitle}
                onClose={handleCloseOverlay}
              />
            )}
            <div className="v2-map-card__controls">
              <div className="v2-map-card__type">
                <button
                  type="button"
                  className={mapType === 'roadmap' ? 'v2-map-card__type--active' : undefined}
                  onClick={() => setMapType('roadmap')}
                >
                  Map
                </button>
                <button
                  type="button"
                  className={mapType === 'satellite' ? 'v2-map-card__type--active' : undefined}
                  onClick={() => setMapType('satellite')}
                >
                  Satellite
                </button>
              </div>
              <div className="v2-map-card__zoom">
                <button
                  type="button"
                  aria-label="Zoom in"
                  onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current.getZoom() || 12) + 1)}
                >
                  +
                </button>
                <button
                  type="button"
                  aria-label="Zoom out"
                  onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current.getZoom() || 12) - 1)}
                >
                  −
                </button>
              </div>
            </div>
            <div className="v2-map-card__badge">
              <span aria-hidden>📍</span>
              {badgeLabel}
            </div>
          </>
        ) : (
          <div className="v2-map-card__fallback">
            <p>Map preview unavailable</p>
            <span>Add REACT_APP_GOOGLE_MAPS_API_KEY to enable the map.</span>
          </div>
        )}
      </div>

      {!mapOnly && (
        <PlaceDetailsPanel
          place={selectedPlace}
          isLoading={isLoadingPlace}
          emptyMessage="Select a recommended place, an activity from the day plan, or tap a numbered pin on the map to see photos, hours, and details."
        />
      )}
    </div>
  );
};

export default TripMapSidebar;
